"use client";

import React from "react";
import {
  BarChart as BC,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { Props } from "recharts/types/cartesian/XAxis";

import {
  CustomBarCursor,
  CustomTooltip,
  CustomXAxisTick,
} from "@/utils/recharts-widgets";

import { BarChartProps } from "./BarChartProps";
import BarYAxis from "./BarYAxis";
import { assignYAxisIdToInfo } from "./utils";

/**
 * 這個長條圖元件除了渲染一般的長條圖以外，還可以做到：
 * 1. 包含正數和負數的長條圖
 * 2. 無限加入 value，自動生成多個 bar
 * 3. 無限疊加 stack，自動生成 stacked bar chart
 *
 * @param data
 * 資料格式為：
 * ```
 * [{ name: "A", value: 10, value1: 13, ... valueN: 4 },
 *  { name: "B", value: 13, value1: 17, ... valueN: 6 }]
 * ```
 *
 * @param info
 * 我們需要提供資料中每個 name 所擁有的 value 的 barColor 和 stackId：
 * ```
 * {
 *   value: { barColor: "red", stackId: "1", interval: 5 },
 *   value1: { barColor: "blue", stackId: "2", interval: 10 },
 *   ...
 *   valueN: { barColor: "green", stackId: "n", interval: n }
 * }
 *  ```
 */
const BarChart = ({
  syncId = 0,
  data,
  info,
  yAxisWidth = 60,
  hideYAxis = false,
  xTicksFormatter,
  yTicksFormatter,
  xTicksPaddingTop = 0,
  yTicksPaddingRight = 12,
  yPaddingTop = 0,
  yPaddingBottom = 0,
  xPaddingLeft = 12,
  xPaddingRight = 12,
  isXTicksRotate,
  xTicksAnchor,
  gridLineColor = "#F2F2F2",
  cursorColor = "#F2F2F2",
  axisTickColor = "#676767",
  showYAxisLine = false,
  yAxisInterval = "preserveEnd",
  xAxisInterval = "preserveEnd",
  biAxial = "none",
  ticks,
  tooltipIconType,
  showLegend = false,
  legendProps,
  tooltipHideInfo,
  tooltipReverseOrder,
  reverseStackOrder = false,
  YAxisDomain,
  tooltipSortMethod,
}: BarChartProps) => {
  if (data?.length === 0) return <></>;

  if (biAxial !== "none") {
    if (Array.from(info.entries()).length !== 2)
      return <>Data size should be 2 in BiAxial</>;

    info = assignYAxisIdToInfo(info, biAxial);
  }
  const [firstInfo, secondInfo] = Array.from(info.values());

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BC
        data={data}
        syncId={syncId}
        syncMethod="value"
        reverseStackOrder={reverseStackOrder}
      >
        <XAxis
          tick={(props: Props) => (
            <CustomXAxisTick
              {...{
                props,
                xTicksPaddingTop,
                xTicksFormatter,
                axisTickColor,
                isTickRotate: isXTicksRotate,
                textAnchor: xTicksAnchor,
              }}
            />
          )}
          dataKey="name"
          tickLine={false}
          interval={xAxisInterval}
          padding={{ left: xPaddingLeft, right: xPaddingRight }}
        />

        {BarYAxis({
          yTicksPaddingRight,
          yTicksFormatter,
          axisTickColor: firstInfo.barColor,
          yAxisWidth,
          ticks,
          yAxisInterval,
          showYAxisLine,
          yPaddingTop,
          yPaddingBottom,
          yId: firstInfo.yAxisId,
          hideAxis: hideYAxis,
          domain: YAxisDomain,
        })}

        {biAxial !== "none" &&
          BarYAxis({
            yTicksPaddingRight: -yTicksPaddingRight,
            yTicksFormatter,
            axisTickColor: secondInfo.barColor,
            yAxisWidth,
            ticks,
            yAxisInterval,
            showYAxisLine,
            yPaddingTop,
            yPaddingBottom,
            yId: secondInfo.yAxisId,
          })}

        <CartesianGrid vertical={false} stroke={gridLineColor} />

        <Tooltip
          wrapperStyle={{ outline: "none" }}
          content={
            <CustomTooltip
              valueFormatter={yTicksFormatter}
              iconType={tooltipIconType}
              hideInfo={tooltipHideInfo}
              reverseOrder={tooltipReverseOrder}
              tooltipSortMethod={tooltipSortMethod}
            />
          }
          cursor={<CustomBarCursor fill={cursorColor} />}
        />

        {Array.from(info).map(([key, barInfo]) => (
          <Bar
            isAnimationActive={false}
            key={key}
            dataKey={key}
            fill={barInfo.barColor}
            stackId={barInfo.stackId ?? key}
            yAxisId={barInfo.yAxisId}
          >
            {barInfo.negativeColor != null &&
              data.map((entry) => (
                <Cell
                  key={`cell-${key}`}
                  fill={
                    (entry[key] as number) < 0
                      ? barInfo.negativeColor
                      : barInfo.barColor
                  }
                />
              ))}
          </Bar>
        ))}
        {showLegend && (
          <Legend
            {...{
              iconType: "square",
              align: "center",
              verticalAlign: "top",
              wrapperStyle: { fontSize: "14px" },
            }}
            {...legendProps}
          />
        )}
      </BC>
    </ResponsiveContainer>
  );
};

export default React.memo(BarChart);
